/**
 * The parallax arithmetic (omega-parallax.js coverCrop/measure/apply, L230–424;
 * docs/behavior/hero-and-motion.md §6.4–6.5). Pure: no DOM access.
 */
import { CROP_K, LIVE_MARGIN, MIN_CROP, MIN_GAP, MIN_GAP_PLAIN, SLACK_K } from './constants';

export type LayerKind = 'pan' | 'loose' | 'card' | 'block' | 'head';
export type PanAxis = 'v' | 'h';

export interface Crop {
  readonly v: number;
  readonly h: number;
}

/** The overhang an `object-fit: cover` image is already throwing away, per side, minus 1 px. */
export function coverCrop(
  objectFit: string,
  naturalWidth: number,
  naturalHeight: number,
  width: number,
  height: number,
): Crop | null {
  if (objectFit !== 'cover') return null;
  if (!naturalWidth || !naturalHeight || width < 1 || height < 1) return null;
  const k = Math.max(width / naturalWidth, height / naturalHeight);
  return {
    v: Math.max(0, (naturalHeight * k - height) / 2 - 1),
    h: Math.max(0, (naturalWidth * k - width) / 2 - 1),
  };
}

/** Pan (with its axis) when there is at least 8 px of crop, otherwise a loose float. */
export function classifyImage(
  crop: Crop | null,
): { readonly kind: 'pan'; readonly axis: PanAxis } | { readonly kind: 'loose' } {
  if (crop !== null && (crop.v >= MIN_CROP || crop.h >= MIN_CROP)) {
    return { kind: 'pan', axis: crop.v >= MIN_CROP ? 'v' : 'h' };
  }
  return { kind: 'loose' };
}

/** Amplitude the depth asks for, before slack: |depth|·(vh + h)·0.5·taper, capped. */
export function wantedAmplitude(depth: number, vh: number, h: number, taper: number, cap: number): number {
  const want = Math.abs(depth) * (vh + h) * 0.5 * taper;
  return want > cap ? cap : want;
}

/** Slack of a pan layer along its axis. */
export function panSlack(crop: Crop | null, axis: PanAxis): number {
  return crop ? (axis === 'v' ? crop.v : crop.h) * CROP_K : 0;
}

/** Slack of a one-way (loose) layer: only the gap above is ever consumed. */
export function oneWaySlack(gapAbove: number): number {
  return gapAbove * SLACK_K;
}

/** Slack of a symmetric layer: nothing when the gap is under what its neighbourhood needs. */
export function neighbourSlackFor(gap: number, revealNear: boolean): number {
  const need = revealNear ? MIN_GAP : MIN_GAP_PLAIN;
  return gap < need ? 0 : gap * SLACK_K;
}

/** Final signed amplitude: the wanted travel limited by the slack, sign of the depth. */
export function finalAmplitude(want: number, slack: number, depth: number): number {
  let w = want;
  if (w > slack) w = slack;
  if (w < 0) w = 0;
  return depth < 0 ? -w : w;
}

export function isLive(top: number, h: number, scrollY: number, vh: number): boolean {
  return top - scrollY < vh + LIVE_MARGIN && top + h - scrollY > -LIVE_MARGIN;
}

/** Scroll progress of the element's centre against the viewport centre, clamped to ±0.5. */
export function scrollProgress(top: number, h: number, scrollY: number, vh: number, oneWay: boolean): number {
  const vpMid = scrollY + vh * 0.5;
  const mid = top + h * 0.5;
  let p = (mid - vpMid) / (vh + h);
  if (p > 0.5) p = 0.5;
  else if (p < -0.5) p = -0.5;
  if (oneWay && p < 0) p = 0;
  return p;
}

/** Rounded offset for this progress: pans to 0.1 px, translates to 0.01 px. */
export function roundedOffset(p: number, amp: number, kind: LayerKind): number {
  const d = -p * amp * 2;
  return kind === 'pan' ? Math.round(d * 10) / 10 : Math.round(d * 100) / 100;
}

export function objectPositionValue(n: number, axis: PanAxis): string {
  if (n === 0) return '';
  return axis === 'v' ? '50% calc(50% + ' + n + 'px)' : 'calc(50% + ' + n + 'px) 50%';
}

export function translateValue(t: number): string {
  return t === 0 ? '' : '0px ' + t + 'px';
}
